import {
  TypeCoursePlayerFields,
  TypeCourseSubChapter,
} from '@/core/generated-types';
import {
  TypeMediaAssetRecord,
  TypePlaybackHistoryRecord,
} from '@/lib/api/playbackService/types';
import { TypePlaybackHistoryMutations } from '@/lib/api/playbackService/hooks/useCoursePlaybackHistories';

export interface CoursePlayerProps extends TypeCoursePlayerFields {
  // [videos] required for CoursePlayer to function.
  //    Without this we cannot get tokens to stream mux assets or report on playback histories.
  videos: TypeMediaAssetRecord[];
  songs?: TypeMediaAssetRecord[] | undefined;

  // [initialVideoId] optional.
  //   by default CoursePlayer will load the first video in the course in the player.
  //   This allows for an override for returning users so we can load the last video they were viewing.
  initialVideoId?: string | undefined;

  // [playbackHistories] optional.
  //   Because a new course user will not have any playback histories, we need to support this use case
  playbackHistories?: TypePlaybackHistoryRecord[] | undefined;
  // mutations that can be performed on a playback history record
  playbackHistoryMutations: TypePlaybackHistoryMutations;
}

export type CourseSubChapterProgressBar = {
  subChapterId: string;
  percentageComplete: number;
  isComplete: boolean;
};

/**
 * Identify and return the last updated playback history for this course
 *
 *  NOTE: exported for testing purposes only
 */
export function _getLatestPlaybackHistory(
  playbackHistories: TypePlaybackHistoryRecord[] | undefined,
) {
  if (!playbackHistories) {
    return null;
  }

  const editablePlaybackHistories = [...playbackHistories];
  function sortByUpdatedDateDescending(
    a: TypePlaybackHistoryRecord,
    b: TypePlaybackHistoryRecord,
  ) {
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  }

  const [latestPlaybackHistory] =
    editablePlaybackHistories.sort(sortByUpdatedDateDescending) || [];

  return latestPlaybackHistory;
}

/**
 * Identifies and returns the playbackId of the first video that will be
 * loaded in the mux player. This will either be:
 *   1) the last video the user played in the course,
 *      identified by updated_at timestamp on playback_histories
 *   OR
 *
 *   2) fallback to the first video in the course
 *
 *   NOTE: exported for testing purposes only
 */
export function _getInitialVideoId(
  mediaAssets: TypeMediaAssetRecord[],
  playbackHistories: TypePlaybackHistoryRecord[] | undefined,
): string | undefined {
  const latestPlaybackHistory = _getLatestPlaybackHistory(playbackHistories);
  return mediaAssets?.find(
    (video) => video.id === latestPlaybackHistory?.media_asset_id,
  )?.cms_id;
}

export function _findVideo(
  mediaAssets: TypeMediaAssetRecord[],
  subChapter?: TypeCourseSubChapter | null,
) {
  if (!subChapter) return null;

  const { id } = subChapter.fields.video.sys;
  return mediaAssets.find((video) => video.cms_id === id);
}

export function _findPlaybackHistory(
  playbackHistories: TypePlaybackHistoryRecord[] | undefined,
  mediaAsset?: TypeMediaAssetRecord | null,
) {
  if (!playbackHistories || !mediaAsset) return null;

  return playbackHistories.find(
    (playbackHistory) => playbackHistory.media_asset_id === mediaAsset.id,
  );
}
